import { createTheme } from '@mui/material/styles';

// Create a light theme based on the screenshot colors
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#004aad', // Deep blue from screenshot
      light: '#3373c4',
      dark: '#003580',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#A2C3A4', // Green from screenshot
      light: '#cbf199', // Light green from screenshot
      dark: '#869d96', // Muted green from screenshot
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff', // Pure white background
      paper: '#ffffff',
      pattern: 'transparent', // Remove pattern
    },
    text: {
      primary: '#333333', // Dark text from screenshot
      secondary: '#666666', // Medium gray for secondary text
      disabled: '#9CA3AF',
    },
    divider: '#E5E7EB',
    grey: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.12)',
    '0px 3px 6px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.12)',
    '0px 10px 20px rgba(0, 0, 0, 0.08), 0px 6px 12px rgba(0, 0, 0, 0.12)',
    '0px 14px 28px rgba(0, 0, 0, 0.08), 0px 8px 16px rgba(0, 0, 0, 0.12)',
    '0px 18px 36px rgba(0, 0, 0, 0.08), 0px 10px 20px rgba(0, 0, 0, 0.12)',
    '0px 24px 48px rgba(0, 0, 0, 0.08), 0px 12px 24px rgba(0, 0, 0, 0.12)',
    '0px 32px 64px rgba(0, 0, 0, 0.08), 0px 16px 32px rgba(0, 0, 0, 0.12)',
    '0px 40px 80px rgba(0, 0, 0, 0.08), 0px 20px 40px rgba(0, 0, 0, 0.12)',
    '0px 48px 96px rgba(0, 0, 0, 0.08), 0px 24px 48px rgba(0, 0, 0, 0.12)',
    '0px 56px 112px rgba(0, 0, 0, 0.08), 0px 28px 56px rgba(0, 0, 0, 0.12)',
    '0px 64px 128px rgba(0, 0, 0, 0.08), 0px 32px 64px rgba(0, 0, 0, 0.12)',
    '0px 72px 144px rgba(0, 0, 0, 0.08), 0px 36px 72px rgba(0, 0, 0, 0.12)',
    '0px 80px 160px rgba(0, 0, 0, 0.08), 0px 40px 80px rgba(0, 0, 0, 0.12)',
    '0px 88px 176px rgba(0, 0, 0, 0.08), 0px 44px 88px rgba(0, 0, 0, 0.12)',
    '0px 96px 192px rgba(0, 0, 0, 0.08), 0px 48px 96px rgba(0, 0, 0, 0.12)',
    '0px 104px 208px rgba(0, 0, 0, 0.08), 0px 52px 104px rgba(0, 0, 0, 0.12)',
    '0px 112px 224px rgba(0, 0, 0, 0.08), 0px 56px 112px rgba(0, 0, 0, 0.12)',
    // Add definition for elevation 24
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
          '&:hover': {
            boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

// Create a dark theme based on the screenshot colors
const darkTheme = createTheme({
  ...lightTheme,
  palette: {
    ...lightTheme.palette,
    mode: 'dark',
    primary: {
      main: '#004aad', // Deep blue from screenshot
      light: '#3373c4',
      dark: '#003580',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#A2C3A4', // Green from screenshot
      light: '#cbf199', // Light green from screenshot
      dark: '#869d96', // Muted green from screenshot
      contrastText: '#ffffff',
    },
    background: {
      default: '#1A1A2A', // Darker background for dark mode
      paper: '#242436',
    },
    text: {
      primary: '#ffffff',
      secondary: '#A2C3A4', // Using the green for secondary text in dark mode
      disabled: '#6B7280',
    },
    divider: '#374151',
  },
});

export { lightTheme, darkTheme };
